import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  query,
  orderBy,
  writeBatch,
  Timestamp
} from 'firebase/firestore'
import { db } from './config'
import { COLLECTIONS } from '@/constants'
import type { BsnlCustomer, BsnlCustomerInput, ApiResponse } from '@/types'
import rawCustomers from '@/constants/customers.json'

const getDb = () => {
  if (!db) throw new Error('Firebase is not configured.')
  return db
}

const fromFirestore = (id: string, data: Record<string, any>): BsnlCustomer => ({
  id,
  category: data.category ?? '',
  exchangeCode: data.exchangeCode ?? '',
  serviceNumber: data.serviceNumber ?? '',
  subServiceType: data.subServiceType ?? '',
  subscriptionPlan: data.subscriptionPlan ?? '',
  planPeriod: data.planPeriod ?? '',
  customerName: data.customerName ?? '',
  billingAccountNo: Number(data.billingAccountNo ?? 0),
  mobile: Number(data.mobile ?? 0),
  email: data.email ?? null,
  address: data.address ?? '',
  fmc: Number(data.fmc ?? 0),
  oltIp: data.oltIp ?? '',
  bbUserId: data.bbUserId ?? '',
  voipPassword: data.voipPassword ?? null,
  vlan: data.vlan ? Number(data.vlan) : null,
  port: data.port ? Number(data.port) : null,
  building: data.building ?? '',
  activationDate: data.activationDate ?? '',
  createdAt: (data.createdAt as Timestamp)?.toDate?.() ?? new Date(),
  updatedAt: (data.updatedAt as Timestamp)?.toDate?.() ?? new Date()
})

export const customersService = {
  async list(): Promise<ApiResponse<BsnlCustomer[]>> {
    try {
      const q = query(collection(getDb(), COLLECTIONS.CUSTOMERS), orderBy('customerName', 'asc'))
      const snap = await getDocs(q)
      
      // Auto seed if empty
      if (snap.empty) {
        console.log('[Customers] Collection is empty. Auto-seeding initial database from Excel raw data...')
        const seedResult = await this.seedBsnlCustomers(true)
        if (seedResult.success) {
          const reSnap = await getDocs(q)
          return { success: true, data: reSnap.docs.map(d => fromFirestore(d.id, d.data())) }
        }
      }
      
      // Auto-detect duplicates or incorrect count of records
      const serviceNums = snap.docs.map(d => d.data().serviceNumber).filter(Boolean)
      const hasDuplicates = serviceNums.length !== new Set(serviceNums).size
      
      // If we have duplicates, auto-correct by performing a forced re-seed
      if (hasDuplicates || snap.size > 105) {
        console.warn(`[Customers] Detected duplicate or redundant customer records (total: ${snap.size}). Auto-correcting collection to match Excel spreadsheet source of truth...`)
        const correctionResult = await this.seedBsnlCustomers(true)
        if (correctionResult.success) {
          const reSnap = await getDocs(q)
          return { success: true, data: reSnap.docs.map(d => fromFirestore(d.id, d.data())) }
        }
      }
      
      return { success: true, data: snap.docs.map(d => fromFirestore(d.id, d.data())) }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to fetch customer data' }
    }
  },

  async get(id: string): Promise<ApiResponse<BsnlCustomer>> {
    try {
      const snap = await getDoc(doc(getDb(), COLLECTIONS.CUSTOMERS, id))
      if (!snap.exists()) return { success: false, error: 'Customer not found' }
      return { success: true, data: fromFirestore(snap.id, snap.data()) }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to fetch customer details' }
    }
  },

  async create(input: BsnlCustomerInput): Promise<ApiResponse<BsnlCustomer>> {
    try {
      const docId = input.serviceNumber.trim().replace(/[\s/]/g, '_')
      const docRef = doc(getDb(), COLLECTIONS.CUSTOMERS, docId)
      
      // Check if duplicate service number exists
      const existing = await getDoc(docRef)
      if (existing.exists()) {
        return { success: false, error: 'A customer with this Service Number already exists.' }
      }

      const clean = Object.fromEntries(
        Object.entries(input).filter(([, v]) => v !== undefined)
      )
      await setDoc(docRef, {
        ...clean,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
      const snap = await getDoc(docRef)
      return { success: true, data: fromFirestore(snap.id, snap.data()!) }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to create customer' }
    }
  },

  async update(id: string, data: Partial<BsnlCustomerInput>): Promise<ApiResponse<void>> {
    try {
      const clean = Object.fromEntries(
        Object.entries(data).filter(([, v]) => v !== undefined)
      )
      await updateDoc(doc(getDb(), COLLECTIONS.CUSTOMERS, id), {
        ...clean,
        updatedAt: serverTimestamp()
      })
      return { success: true }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to update customer' }
    }
  },

  async remove(id: string): Promise<ApiResponse<void>> {
    try {
      await deleteDoc(doc(getDb(), COLLECTIONS.CUSTOMERS, id))
      return { success: true }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to delete customer' }
    }
  },

  async seedBsnlCustomers(force = false): Promise<ApiResponse<number>> {
    try {
      const colRef = collection(getDb(), COLLECTIONS.CUSTOMERS)
      const snap = await getDocs(colRef)
      
      if (!force && snap.size === 100) {
        // Double check there are no duplicate service numbers
        const serviceNums = snap.docs.map(d => d.data().serviceNumber).filter(Boolean)
        const hasDuplicates = serviceNums.length !== new Set(serviceNums).size
        if (!hasDuplicates) {
          return { success: true, data: 0, message: 'Database already has exactly 100 clean customer records.' }
        }
      }

      // Purge all existing records to clear out duplicates or old auto-generated ID records
      if (!snap.empty) {
        console.log(`[Customers] Purging ${snap.size} records from firestore collection to ensure clean database...`)
        
        // Chunk deletes into batches of 400 to prevent exceeding Firestore batch limits
        const docs = snap.docs
        for (let i = 0; i < docs.length; i += 400) {
          const deleteBatch = writeBatch(getDb())
          const chunk = docs.slice(i, i + 400)
          chunk.forEach(d => {
            deleteBatch.delete(d.ref)
          })
          await deleteBatch.commit()
        }
      }

      // Seed all 100 raw customers using deterministic Document IDs to avoid duplicates
      console.log(`[Customers] Seeding ${rawCustomers.length} records with serviceNumber key...`)
      const seedBatch = writeBatch(getDb())
      
      rawCustomers.forEach((cust) => {
        const docId = cust.serviceNumber.trim().replace(/[\s/]/g, '_')
        const docRef = doc(getDb(), COLLECTIONS.CUSTOMERS, docId)
        seedBatch.set(docRef, {
          ...cust,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        })
      })

      await seedBatch.commit()
      return { success: true, data: rawCustomers.length, message: `Successfully cleared database and seeded ${rawCustomers.length} clean customer records.` }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Seeding failed' }
    }
  }
}
export default customersService
